<template>
    <v-row justify="center">
        <!-- Plant Species Dialog -->
        <PlantSpeciesEditDialog
            :dialog.sync="plantSpeciesDialog"
            v-on:item-created="plantSpeciesAdded"
        ></PlantSpeciesEditDialog>

        <!-- Plant Color Dialog -->
        <ColorEditDialog
            :dialog.sync="colorDialog"
            v-on:item-created="colorAdded"
        ></ColorEditDialog>

        <!-- Plant Sort Dialog -->
        <TagEditDialog
            :dialog.sync="tagDialog"
            v-on:item-created="tagAdded"
        ></TagEditDialog>

        <!-- Plant Environment Dialog -->
        <EnvironmentEditDialog
            :dialog.sync="environmentDialog"
            v-on:item-created="environmentAdded"
        ></EnvironmentEditDialog>

        <!-- Pot Size Dialog -->
        <PotSizeEditDialog
            :dialog.sync="potSizeDialog"
            v-on:item-created="potSizeAdded"
        ></PotSizeEditDialog>

        <!-- Supplier Dialog -->
        <SupplierEditDialog
            :dialog.sync="supplierDialog"
            v-on:item-created="supplierAdded"
        ></SupplierEditDialog>

        <!-- Main dialog -->
        <v-dialog v-model="dialog" persistent max-width="1000px">
            <v-card>
                <v-alert class="mb-0" v-if="error" tile type="error">{{ error }}</v-alert>
                <v-card-title class="primary white--text">{{
                    formTitle
                }}</v-card-title>
                <v-card-text class="mt-4">
                    <v-form ref="form">
                        <v-row align="center">
                            <!-- Plant Species -->
                            <v-col cols="12" md="4">
                                <v-autocomplete
                                    v-model="editedItem.plantSpeciesNodeId"
                                    :items="plantSpecies"
                                    item-text="node.name"
                                    item-value="node.id"
                                    :loading="isLoadingPlantSpecies"
                                    :rules="[rules.required]"
                                    label="Plant Species*"
                                    class="subtitle-2"
                                    outlined
                                >
                                    <template
                                        v-if="hasPermission('add_plantspecies')"
                                        v-slot:append-outer
                                    >
                                        <v-btn text small tabindex="-1">
                                            <v-icon
                                                color="grey"
                                                @click="addPlantSpecies"
                                                tabindex="-1"
                                                >add</v-icon
                                            >
                                        </v-btn>
                                    </template>
                                </v-autocomplete>
                            </v-col>

                            <!-- Name -->
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="editedItem.name"
                                    :counter="100"
                                    :rules="[
                                        rules.required,
                                        rules.maxLength(50)
                                    ]"
                                    label="Name*"
                                    outlined
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>

                            <!-- Default Supplier -->
                            <v-col cols="12" md="4">
                                <v-autocomplete
                                    v-model="editedItem.defaultSupplierNodeId"
                                    :items="suppliers"
                                    item-text="node.name"
                                    item-value="node.id"
                                    :loading="isLoadingSuppliers"
                                    label="Default Supplier"
                                    class="subtitle-2"
                                    outlined
                                    clearable
                                >
                                    <template
                                        v-if="hasPermission('add_supplier')"
                                        v-slot:append-outer
                                    >
                                        <v-btn text small tabindex="-1">
                                            <v-icon
                                                color="grey"
                                                @click="addSupplier"
                                                tabindex="-1"
                                                >add</v-icon
                                            >
                                        </v-btn>
                                    </template>
                                </v-autocomplete>
                            </v-col>

                            <!-- Plant Pot Sizes -->
                            <v-col cols="12" md="6">
                                <v-autocomplete
                                    v-model="editedItem.selectedPotSizes"
                                    :items="potSizes"
                                    item-text="node.potSizeCm"
                                    item-value="node.id"
                                    :loading="isLoadingPotSizes"
                                    :rules="[rules.listRequired]"
                                    label="Select Pot Sizes*"
                                    class="subtitle-2"
                                    outlined
                                    multiple
                                >
                                    <template
                                        slot="selection"
                                        slot-scope="{ item }"
                                    >
                                        <v-chip color="primary"
                                            >{{
                                                item.node.potSizeCm
                                            }}
                                            cm</v-chip
                                        >
                                    </template>
                                    <template
                                        v-if="hasPermission('add_potsize')"
                                        v-slot:append-outer
                                    >
                                        <v-btn text small tabindex="-1">
                                            <v-icon
                                                color="grey"
                                                @click="addPotSize"
                                                tabindex="-1"
                                                >add</v-icon
                                            >
                                        </v-btn>
                                    </template>
                                </v-autocomplete>
                            </v-col>

                            <!-- Plant Colors -->
                            <v-col cols="12" md="6">
                                <v-autocomplete
                                    v-model="editedItem.selectedColors"
                                    :items="colors"
                                    item-text="node.color"
                                    item-value="node.id"
                                    :loading="isLoadingColors"
                                    :rules="[rules.listRequired]"
                                    label="Select Colors*"
                                    class="subtitle-2"
                                    outlined
                                    multiple
                                >
                                    <template
                                        slot="selection"
                                        slot-scope="{ item }"
                                    >
                                        <v-chip color="primary">{{
                                            item.node.color
                                        }}</v-chip>
                                    </template>
                                    <template
                                        v-if="hasPermission('add_color')"
                                        v-slot:append-outer
                                    >
                                        <v-btn text small tabindex="-1">
                                            <v-icon
                                                color="grey"
                                                @click="addColor"
                                                tabindex="-1"
                                                >add</v-icon
                                            >
                                        </v-btn>
                                    </template>
                                </v-autocomplete>
                            </v-col>

                            <!-- Plant Environments -->
                            <v-col cols="12" md="6">
                                <v-autocomplete
                                    v-model="editedItem.selectedEnvironments"
                                    :items="environments"
                                    item-text="node.environment"
                                    item-value="node.id"
                                    :loading="isLoadingEnvironments"
                                    :rules="[rules.listRequired]"
                                    label="Select Environments*"
                                    class="subtitle-2"
                                    outlined
                                    multiple
                                >
                                    <template
                                        slot="selection"
                                        slot-scope="{ item }"
                                    >
                                        <v-chip color="primary">{{
                                            item.node.environment
                                        }}</v-chip>
                                    </template>
                                    <template
                                        v-if="hasPermission('add_environment')"
                                        v-slot:append-outer
                                    >
                                        <v-btn text small tabindex="-1">
                                            <v-icon
                                                color="grey"
                                                @click="addEnvironment"
                                                tabindex="-1"
                                                >add</v-icon
                                            >
                                        </v-btn>
                                    </template>
                                </v-autocomplete>
                            </v-col>

                            <!-- Plant Tags -->
                            <v-col cols="12" md="6">
                                <v-autocomplete
                                    v-model="editedItem.selectedTags"
                                    :items="tags"
                                    item-text="node.tag"
                                    item-value="node.id"
                                    :loading="isLoadingTags"
                                    :rules="[rules.listRequired]"
                                    label="Select Tags*"
                                    class="subtitle-2"
                                    outlined
                                    multiple
                                >
                                    <template
                                        slot="selection"
                                        slot-scope="{ item }"
                                    >
                                        <v-chip color="primary">{{
                                            item.node.tag
                                        }}</v-chip>
                                    </template>
                                    <template
                                        v-if="hasPermission('add_tag')"
                                        v-slot:append-outer
                                    >
                                        <v-btn text small tabindex="-1">
                                            <v-icon
                                                color="grey"
                                                @click="addTag"
                                                tabindex="-1"
                                                >add</v-icon
                                            >
                                        </v-btn>
                                    </template>
                                </v-autocomplete>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions class="pb-5 pr-5">
                    <v-spacer></v-spacer>
                    <v-btn text rounded @click="close">Cancel</v-btn>
                    <v-btn
                        outlined
                        rounded
                        :loading="isSaving"
                        color="primary"
                        @click="save"
                        >Save</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import helper from "@/utils/helper.js";
import PlantSpeciesEditDialog from "@/components/plants/PlantSpeciesEditDialog.vue";
import ColorEditDialog from "@/components/plants/ColorEditDialog.vue";
import TagEditDialog from "@/components/plants/TagEditDialog.vue";
import EnvironmentEditDialog from "@/components/plants/EnvironmentEditDialog.vue";
import PotSizeEditDialog from "@/components/plants/PotSizeEditDialog.vue";
import SupplierEditDialog from "@/components/tenants/SupplierEditDialog.vue";

export default {
    name: "plant-edit-dialog",
    props: ["dialog", "object"],
    components: {
        PlantSpeciesEditDialog,
        ColorEditDialog,
        TagEditDialog,
        EnvironmentEditDialog,
        PotSizeEditDialog,
        SupplierEditDialog
    },
    data() {
        return {
            error: null,
            isLoadingPlantSpecies: false,
            isLoadingSuppliers: false,
            isLoadingColors: false,
            isLoadingEnvironments: false,
            isLoadingPotSizes: false,
            isLoadingTags: false,
            isSaving: false,
            plantSpeciesDialog: false,
            colorDialog: false,
            tagDialog: false,
            environmentDialog: false,
            potSizeDialog: false,
            supplierDialog: false,
            editedItem: {
                id: "",
                name: "",
                plantSpeciesNodeId: "",
                selectedColors: [],
                selectedPotSizes: [],
                selectedEnvironments: [],
                selectedTags: [],
                defaultSupplierNodeId: ""
            },
            defaultItem: {
                id: "",
                name: "",
                plantSpeciesNodeId: "",
                selectedColors: [],
                selectedPotSizes: [],
                selectedEnvironments: [],
                selectedTags: [],
                defaultSupplierNodeId: ""
            },
            rules: {
                required: v => !helper.isEmpty(v) || "This field is required",
                listRequired: v =>
                    (v || "").length >= 1 || `This field is required`,
                minLength: len => v =>
                    (v || "").length >= len || `Require at least ${len}`,
                maxLength: len => v => (v || "").length <= len || "Too long"
                // nameRules: [
                //     v => !!v || "Name is required",
                //     v =>
                //         v.length <= 50 || "Name must be less than 50 characters"
                // ]
            }
            // departmentDialog: false,
            // plantVariantDialog: false
        };
    },
    computed: {
        plantSpecies: function() {
            return this.$store.state.plantSpecies.plantSpecies;
        },
        suppliers: function() {
            return this.$store.state.suppliers.suppliers;
        },
        colors: function() {
            return this.$store.state.colors.colors;
        },
        potSizes: function() {
            return this.$store.state.potSizes.potSizes;
        },
        environments: function() {
            return this.$store.state.environments.environments;
        },
        tags: function() {
            return this.$store.state.tags.tags;
        },
        formTitle() {
            return this.object ? "Edit Plant" : "New Plant";
        }
    },
    created() {
        this.hasPermission = helper.hasPermission;
    },
    watch: {
        // reset form when dialog open or close
        dialog(val) {
            // if open dialog, initialize selections
            if (val) {
                this.initializeSelections();
            }

            this.resetForm();
        }
    },
    methods: {
        initializeSelections() {
            // fetch plant species
            this.isLoadingPlantSpecies = true;
            this.$store
                .dispatch("plantSpecies/fetchPlantSpecies")
                .then(() => {})
                .catch(error => {
                    /*handle errors*/
                    console.log(error);
                })
                .finally(() => (this.isLoadingPlantSpecies = false));

            // fetch colors
            this.isLoadingColors = true;
            this.$store
                .dispatch("colors/fetchColors")
                .then(() => {})
                .catch(error => {
                    console.log(error);
                })
                .finally(() => (this.isLoadingColors = false));

            // fetch potsizes
            this.isLoadingPotSizes = true;
            this.$store
                .dispatch("potSizes/fetchPotSizes")
                .then(() => {})
                .catch(error => {
                    console.log(error);
                })
                .finally(() => (this.isLoadingPotSizes = false));

            // fetch environments
            this.isLoadingEnvironments = true;
            this.$store
                .dispatch("environments/fetchEnvironments")
                .then(() => {})
                .catch(error => {
                    console.log(error);
                })
                .finally(() => (this.isLoadingEnvironments = false));

            // fetch tags
            this.isLoadingTags = true;
            this.$store
                .dispatch("tags/fetchTags")
                .then(() => {})
                .catch(error => {
                    console.log(error);
                })
                .finally(() => (this.isLoadingTags = false));

            // fetch suppliers
            this.isLoadingSuppliers = true;
            this.$store
                .dispatch("suppliers/fetchSuppliers")
                .then(() => {})
                .catch(error => {
                    /*handle errors*/
                    console.log(error);
                })
                .finally(() => (this.isLoadingSuppliers = false));
        },

        resetForm() {
            // reset form state
            this.error = null;
            this.isSaving = false;
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }

            // when editing, set form with object
            if (this.dialog && this.object) {
                this.editedItem.id = this.object.id;
                this.editedItem.name = this.object.name;
                this.editedItem.plantSpeciesNodeId = this.object.plantSpecies.id;
                this.editedItem.selectedColors = this.object.plantColorSet
                    ? this.object.plantColorSet.edges.map(item => {
                          return item.node.color.id;
                      })
                    : [];
                this.editedItem.selectedPotSizes = this.object.plantPotSizeSet
                    ? this.object.plantPotSizeSet.edges.map(item => {
                          return item.node.potSize.id;
                      })
                    : [];
                this.editedItem.selectedEnvironments = this.object
                    .plantEnvironmentSet
                    ? this.object.plantEnvironmentSet.edges.map(item => {
                          return item.node.environment.id;
                      })
                    : [];
                this.editedItem.selectedTags = this.object.plantTagSet
                    ? this.object.plantTagSet.edges.map(item => {
                          return item.node.tag.id;
                      })
                    : [];
                this.editedItem.defaultSupplierNodeId = this.object
                    .defaultSupplier
                    ? this.object.defaultSupplier.id
                    : "";
            }
            // when new or closing, set form with default
            else {
                this.editedItem = Object.assign({}, this.defaultItem);
            }
        },

        close() {
            this.$emit("update:dialog", false);
            this.resetForm();
        },

        save() {
            // check if form valid
            if (!this.$refs.form.validate()) {
                return;
            }

            // set form state
            this.error = null;
            this.isSaving = true;

            // prepare api call payload
            var payload = {
                name: this.editedItem.name,
                plantSpeciesNodeId: this.editedItem.plantSpeciesNodeId,
                colors: this.editedItem.selectedColors,
                potSizes: this.editedItem.selectedPotSizes,
                environments: this.editedItem.selectedEnvironments,
                tags: this.editedItem.selectedTags,
                defaultSupplierNodeId:
                    this.editedItem.defaultSupplierNodeId || ""
            };

            // if editing
            if (this.object) {
                // attach object id
                payload.id = this.editedItem.id;

                // call update api
                this.$store
                    .dispatch("plants/updatePlant", payload)
                    .then(response => {
                        // update parent object
                        this.$emit("update:object", response);
                        this.close();
                    })
                    .catch(error => {
                        this.error = error.graphQLErrors[0].message;
                        this.isSaving = false;
                    })
                    .finally(() => {});
            }
            // if new
            else {
                // create plant via store action
                this.$store
                    .dispatch("plants/createPlant", payload)
                    .then(response => {
                        this.$emit("item-created", response);
                        this.close();
                    })
                    .catch(error => {
                        this.error = error.graphQLErrors
                            .map(error => error.message)
                            .join(", ");
                        this.isSaving = false;
                    })
                    .finally(() => {});
            }
        },

        addPlantSpecies() {
            this.plantSpeciesDialog = true;
        },
        plantSpeciesAdded(item) {
            this.editedItem.plantSpeciesNodeId = item.id;
        },

        addColor() {
            this.colorDialog = true;
        },
        colorAdded(item) {
            this.editedItem.colorNodeId = item.id;
        },

        addTag() {
            this.tagDialog = true;
        },
        tagAdded(item) {
            this.editedItem.tagNodeId = item.id;
        },

        addEnvironment() {
            this.environmentDialog = true;
        },
        environmentAdded(item) {
            this.editedItem.environmentNodeId = item.id;
        },

        addPotSize() {
            this.potSizeDialog = true;
        },
        potSizeAdded(item) {
            this.editedItem.potSizeNodeId = item.id;
        },

        addSupplier() {
            this.supplierDialog = true;
        },
        supplierAdded(item) {
            this.editedItem.defaultSupplierNodeId = item.id;
        }

        // addDepartment() {
        //     this.departmentDialog = true;
        // }
    }
};
</script>